import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StatusIcon from '@mui/icons-material/FiberManualRecord';
import { withStyles } from '@mui/styles';
import { capitalizeFirstLetter } from '../../../../utils/helper';
import { DEFAULT_NULL_REPLACEMENT } from '../../../../utils/constants';
import colors from '../../../../utils/colors';
import ProviderDetails from '../ProviderDetails';

const styles = (theme) => ({
  accordion: {
    marginBottom: '12px !important',
    border: '1px solid #E4E4E4',
    borderRadius: '8px !important',
    boxShadow: 'none !important',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 12px 0 !important',
    },
  },
  accordionSummary: {
    backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    minHeight: '48px !important',
    '&.Mui-expanded': {
      minHeight: '48px !important',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  accordionTitle: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    color: '#000000',
  },
  accordionDetails: {
    padding: '24px',
    display: 'flex',
    flexWrap: 'wrap',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
  },
  fieldLabel: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#828282',
    marginBottom: '4px',
  },
  fieldValue: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    color: '#1F1F1F',
    wordBreak: 'break-word',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  statusIcon: {
    width: 12,
    height: 12,
    marginRight: 4,
  },
  dataSource: {
    background: '#E4E4E4',
    border: '1px solid #C0C0C0',
    boxSizing: 'border-box',
    borderRadius: '24px',
    color: '#1F1F1F',
    fontWeight: 500,
    fontSize: '11px',
    lineHeight: '16px',
    textAlign: 'center',
    padding: '1px 8px',
    display: 'inline-block',
    width: 'fit-content',
  },
  tableContainer: {
    width: '100%',
    marginTop: '8px',
    border: '1px solid #E4E4E4',
    borderRadius: '8px',
  },
  tableHead: {
    backgroundColor: '#F5F5F5',
  },
  tableHeaderCell: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#333333',
    padding: '12px 16px',
    borderBottom: '1px solid #E4E4E4',
  },
  tableBodyCell: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#1F1F1F',
    padding: '12px 16px',
    borderBottom: '1px solid #E4E4E4',
  },
  documentLink: {
    color: colors.BLUE,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  emptyMessage: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#828282',
    fontStyle: 'italic',
  },
});

const customClasses = {
  colWidth: (width) => {
    switch (width?.toLowerCase()) {
      case 'halfwidth':
        return '50%';
      case 'fullwidth':
        return '100%';
      case 'twothird':
        return '66.66%';
      default:
        return '33.33%';
    }
  },
};

const AccordionSection = ({ classes, data, type }) => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const renderFieldValue = (field) => {
    const value = field.value;

    // Handle null, undefined, or dash values
    if (!value || value === '-' || value === DEFAULT_NULL_REPLACEMENT) {
      return <Typography className={classes.emptyMessage}>-</Typography>;
    }

    // Handle status with dot indicator
    if (field.type === 'dot' && field.status_color) {
      return (
        <div className={classes.statusContainer}>
          <StatusIcon htmlColor={field.status_color} className={classes.statusIcon} />
          <Typography className={classes.fieldValue}>
            {capitalizeFirstLetter(value)}
          </Typography>
        </div>
      );
    }

    // Handle pill/data source
    if (field.type === 'pill') {
      return <span className={classes.dataSource}>{value}</span>;
    }

    // Handle currency
    if (field.type === 'currency') {
      const numValue = parseFloat(value);
      const formattedValue = isNaN(numValue) ? '-' : `$ ${numValue.toFixed(2)}`;
      return <Typography className={classes.fieldValue}>{formattedValue}</Typography>;
    }

    // Handle regular string values
    return <Typography className={classes.fieldValue}>{value}</Typography>;
  };

  const renderDocumentList = (documents) => {
    if (!Array.isArray(documents) || documents.length === 0) {
      return <Typography className={classes.emptyMessage}>No documents available</Typography>;
    }

    return (
      <Box>
        {documents.map((doc, index) => (
          <Box key={index} mb={1}>
            <a
              href={doc.location}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.documentLink}
            >
              {doc.name || `Document ${index + 1}`}
            </a>
          </Box>
        ))}
      </Box>
    );
  };

  const renderAccordionContent = () => {
    if (type === 'pcpAccordionSection') {
      // Render PCP information using ProviderDetails component
      if (!data.value || data.value.length === 0) {
        return null;
      }

      // Convert array of fields to object for ProviderDetails component
      const pcpData = {};
      data.value.forEach(field => {
        const fieldName = field.column_name.replace(/\s+/g, '');
        const key = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
        pcpData[key] = field;
      });

      return (
        <Box className={classes.accordionDetails}>
          <ProviderDetails data={pcpData} />
        </Box>
      );
    }

    if (type === 'accordionSection') {
      // Skip rendering if it's PCP accordion with no fields
      if (data.key === 'pcpInformation' && (!data.value || data.value.length === 0)) {
        return null;
      }

      return (
        <Box className={classes.accordionDetails}>
          {data.value && data.value.length > 0 ? (
            data.value.map((field, index) => (
              <Box
                key={index}
                className={classes.fieldContainer}
                style={{ width: customClasses.colWidth(field.width) }}
                pr={field.width?.toLowerCase() === 'fullwidth' ? 0 : 2}
              >
                <Typography className={classes.fieldLabel}>{field.column_name}</Typography>
                {field.type === 'documentList' ? (
                  renderDocumentList(field.value)
                ) : (
                  renderFieldValue(field)
                )}
              </Box>
            ))
          ) : (
            <Typography className={classes.emptyMessage}>No data available</Typography>
          )}
        </Box>
      );
    }

    if (type === 'accordionTableSection') {
      const tableData = data.value || [];
      const tableFields = data.tableFields || [];

      if (tableData.length === 0) {
        return (
          <Box className={classes.accordionDetails}>
            <Typography className={classes.emptyMessage}>No data available</Typography>
          </Box>
        );
      }

      const tableStyles = {
        costTable: {
          width: "100%",
          borderCollapse: "collapse",
          marginTop: "4px",
          backgroundColor: "#fff",
        },
        tableHeader: {
          backgroundColor: "#F8F9FA",
          padding: "12px 16px",
          textAlign: "left",
          fontSize: "14px",
          fontWeight: 600,
          color: "#1F1F1F",
          borderBottom: "1px solid #E4E4E4",
        },
        tableCell: {
          padding: "12px 16px",
          fontSize: "14px",
          color: "#1F1F1F",
          borderBottom: "1px solid #E4E4E4",
        },
        tableCellBold: {
          fontWeight: 500,
        },
      };

      return (
        <Box className={classes.accordionDetails}>
          <table style={tableStyles.costTable}>
            <thead>
              <tr>
                {tableFields.map((field, index) => (
                  <th key={index} style={tableStyles.tableHeader}>
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {tableFields.map((field, colIndex) => {
                    const cellValue = row[field.key];
                    let displayValue = cellValue;

                    // Format currency values
                    if (field.type === 'currency' && cellValue) {
                      displayValue = cellValue;
                    } else if (!cellValue || cellValue === '-') {
                      displayValue = '-';
                    }

                    // Bold the first column (isHeader)
                    const cellStyle = field.isHeader
                      ? { ...tableStyles.tableCell, ...tableStyles.tableCellBold }
                      : tableStyles.tableCell;

                    return (
                      <td key={colIndex} style={cellStyle}>
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    }

    return null;
  };

  // Don't render PCP accordion if it has no fields
  if ((data.key === 'pcpInformation' || type === 'pcpAccordionSection') && (!data.value || data.value.length === 0)) {
    return null;
  }

  const accordionContent = renderAccordionContent();
  if (!accordionContent) return null;

  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded === data.column_name}
      onChange={handleChange(data.column_name)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.accordionSummary}
      >
        <Typography className={classes.accordionTitle}>{data.column_name}</Typography>
      </AccordionSummary>
      <AccordionDetails style={{ padding: 0 }}>
        {accordionContent}
      </AccordionDetails>
    </Accordion>
  );
};

export default withStyles(styles)(AccordionSection);
